import type { Options } from 'ky';
import ky, { HTTPError } from 'ky';

export class FetcherError extends Error {
    status: number;
    data: { detail: string };
    constructor(message: string, status: number, data: { detail: string }) {
        super(message);
        this.status = status;
        this.data = data;
    }
}

export type FetcherOptions = Omit<Options, 'hooks'>;

const getTabId = () => {
    try {
        const stored = sessionStorage.getItem('tabId');
        if (stored) return stored;
        const newId = crypto.randomUUID();
        sessionStorage.setItem('tabId', newId);
        return newId;
    } catch {
        // sessionStorage isn't available (e.g. Safari private mode)
        return 'no-info';
    }
};

const tabId = getTabId();

const fetch = async <T>(url: string, options: FetcherOptions) => {
    try {
        const response = await ky<T>(url, {
            timeout: 20_000,
            ...options,
            hooks: {
                beforeRequest: [
                    ({ request }) => {
                        request.headers.set('X-App-Version', import.meta.env.VITE_APP_VERSION ?? 'localhost');
                        request.headers.set('X-Tab-Id', tabId);
                    },
                ],
            },
        });
        if (response.status === 204 || response.headers.get('content-length') === '0') {
            return undefined as T;
        }
        return response.json();
    } catch (error) {
        if (error instanceof HTTPError) {
            const statusText = error.response.statusText || `Request failed with status code ${error.response.status}`;
            const data: { detail?: string } | undefined = error.data;
            throw new FetcherError(statusText, error.response.status, {
                detail: data?.detail ?? statusText,
            });
        }
        const err = error as Error;
        throw new FetcherError(err.message, 500, { detail: err.message });
    }
};

export const fetcher = {
    get: async <T>(url: string, options: FetcherOptions = {}) => {
        return await fetch<T>(url, { ...options, method: 'GET' });
    },
    post: async <T>(url: string, body: unknown, options: FetcherOptions = {}) => {
        const fetchOptions: FetcherOptions = {
            ...options,
            headers: {
                ...options.headers,
                'Content-Type': 'application/json',
            },
            method: 'POST',
            json: body,
        };
        return await fetch<T>(url, fetchOptions);
    },
    delete: async <T>(url: string, options: FetcherOptions = {}) => {
        return await fetch<T>(url, { ...options, method: 'DELETE' });
    },
};
